import type { OHLCV } from '@shared/types/candle'

import { registerIndicator } from '@/indicators/registry'
import type { IndicatorConfig, IndicatorResult } from '@/types/indicator'
import { IndicatorBase } from '@/types/indicator'

const CONFIG: IndicatorConfig = {
  id: 'camarilla',
  name: 'Camarilla Pivot Levels',
  shortName: 'Cam',
  category: 'professional',
  overlay: true,
  params: [],
  lines: [
    { key: 'h4', label: 'H4', color: '#b71c1c', width: 2 },
    { key: 'h3', label: 'H3', color: '#ef5350', width: 1, style: 'dashed' },
    { key: 'h2', label: 'H2', color: '#ef9a9a', width: 1, style: 'dotted' },
    { key: 'h1', label: 'H1', color: '#ffcdd2', width: 1, style: 'dotted' },
    { key: 'l1', label: 'L1', color: '#b2dfdb', width: 1, style: 'dotted' },
    { key: 'l2', label: 'L2', color: '#80cbc4', width: 1, style: 'dotted' },
    { key: 'l3', label: 'L3', color: '#26a69a', width: 1, style: 'dashed' },
    { key: 'l4', label: 'L4', color: '#00695c', width: 2 },
  ],
}

function buildDailyMap(candles: OHLCV[]): Map<number, { h: number; l: number; c: number }> {
  const m = new Map<number, { h: number; l: number; c: number }>()
  for (const c of candles) {
    const day = Math.floor(c.t / 86400)
    const existing = m.get(day)
    if (!existing) {
      m.set(day, { h: c.h, l: c.l, c: c.c })
    } else {
      if (c.h > existing.h) existing.h = c.h
      if (c.l < existing.l) existing.l = c.l
      existing.c = c.c
    }
  }
  return m
}

class Camarilla extends IndicatorBase {
  readonly config = CONFIG

  calculate(candles: OHLCV[]): IndicatorResult {
    const n = candles.length
    const h4: (number | null)[] = new Array<number | null>(n).fill(null)
    const h3: (number | null)[] = new Array<number | null>(n).fill(null)
    const h2: (number | null)[] = new Array<number | null>(n).fill(null)
    const h1: (number | null)[] = new Array<number | null>(n).fill(null)
    const l1: (number | null)[] = new Array<number | null>(n).fill(null)
    const l2: (number | null)[] = new Array<number | null>(n).fill(null)
    const l3: (number | null)[] = new Array<number | null>(n).fill(null)
    const l4: (number | null)[] = new Array<number | null>(n).fill(null)

    if (n < 2)
      return {
        lines: [
          { key: 'h4', values: h4 },
          { key: 'h3', values: h3 },
          { key: 'h2', values: h2 },
          { key: 'h1', values: h1 },
          { key: 'l1', values: l1 },
          { key: 'l2', values: l2 },
          { key: 'l3', values: l3 },
          { key: 'l4', values: l4 },
        ],
      }

    const dailyMap = buildDailyMap(candles)
    const sortedDays = [...dailyMap.keys()].sort((a, b) => a - b)
    const prevDay = new Map<number, { h: number; l: number; c: number }>()
    for (let i = 1; i < sortedDays.length; i++) {
      prevDay.set(sortedDays[i], dailyMap.get(sortedDays[i - 1])!)
    }

    for (let i = 0; i < n; i++) {
      const day = Math.floor(candles[i].t / 86400)
      const prev = prevDay.get(day)
      if (!prev) continue

      // Camarilla formula: level = C ± 1.1 × (H - L) / divisor
      const range = prev.h - prev.l
      const k = 1.1 * range
      h4[i] = prev.c + k / 2
      h3[i] = prev.c + k / 4
      h2[i] = prev.c + k / 6
      h1[i] = prev.c + k / 12
      l1[i] = prev.c - k / 12
      l2[i] = prev.c - k / 6
      l3[i] = prev.c - k / 4
      l4[i] = prev.c - k / 2
    }

    return {
      lines: [
        { key: 'h4', values: h4 },
        { key: 'h3', values: h3 },
        { key: 'h2', values: h2 },
        { key: 'h1', values: h1 },
        { key: 'l1', values: l1 },
        { key: 'l2', values: l2 },
        { key: 'l3', values: l3 },
        { key: 'l4', values: l4 },
      ],
    }
  }
}

registerIndicator(Camarilla, CONFIG)
